/*
 * improvemint-autoplay.js — the ONE rule for every auto-rotating carousel:
 * it rotates until the shopper uses it, then it stops for the rest of that
 * page view.
 *
 * GP, 2026-10-28, on the home trust band's reviews: "if someone interacts with
 * the arrows then it stops auto-scrolling. (we can apply this for any
 * auto-scrools)". Before this, the home hero kept rotating after an arrow or a
 * dot press (Hyper's slideshow-component sets Swiper's `disableOnInteraction:
 * false`), and the reviews carousel had its own copy of the stop listeners.
 *
 * WHAT COUNTS AS USING IT, inside an autoplay root:
 *   - a press (click, or Enter / Space, which click a button) on an arrow, a dot
 *     or a pagination control: `CONTROL` below. Swiper's A11y module turns Enter
 *     on a focused dot into a click, so keyboard users are covered too;
 *   - a swipe or a drag: a touch or a held mouse button that travels more than
 *     SWIPE_PX sideways, and more sideways than up or down. A tap, and a page
 *     scroll that happens to start on the carousel, do not count;
 *   - a sideways trackpad or shift-wheel scroll.
 * Hovering is not "using it". A carousel may pause on hover and resume on
 * leave (the reviews do), which is its own business.
 *
 * WHAT IS AN AUTOPLAY ROOT (`ROOT` below), so new carousels need no wiring:
 *   - `[data-im-autoplay]` — our own carousels. They listen for the
 *     `im:autoplay-stop` event on that element and stop their own timer
 *     (sections/im-trust-band.liquid);
 *   - `.swiper[data-autoplay]` with a non-zero delay — every Hyper Swiper slider
 *     that renders `data-autoplay` on the element it builds Swiper on
 *     (slideshow-component: sections/im-hero.liquid and Hyper's slideshow;
 *     slideshow-with-product; image-with-text-slider; slider-with-multicolumn),
 *     plus `announcement-bar[autoplay]`. Each of those passes Swiper
 *     `disableOnInteraction: false`, i.e. "keep rotating after a press". Swiper
 *     keeps its instance on the element as `.swiper`, and this file stops that
 *     instance directly, so no Hyper file is edited. Of these, only the home
 *     hero is switched on by a template today (templates/*.json, 2026-10-28).
 * The root gets `data-im-autoplay-stopped` when it stops, and its
 * `--swiper-autoplay-delay` is removed so Hyper's active dot stops filling over
 * the old interval (theme.css falls back to 0.3 s).
 *
 * WHY ONE DOCUMENT-LEVEL LISTENER SET. Capture-phase listeners on `document`
 * see the event before Swiper's own handlers (so an arrow press stops autoplay
 * BEFORE Swiper's slideNext, and the slide change it makes is never treated as
 * a pause to resume from), they work for sections re-rendered by the theme
 * editor, and they work whenever this deferred file arrives, because nothing
 * here has to find the carousel at load time. Movement listeners return at
 * once unless a press started inside a root.
 *
 * Reduced motion is NOT decided here. Each carousel keeps what it already did:
 * the reviews never start under prefers-reduced-motion; the hero's Swiper
 * autoplay does not read the preference (unchanged by this file).
 *
 * Loaded once from layout/theme.liquid; the guard makes a second copy a no-op.
 */
(function () {
  'use strict';

  if (window.ImAutoplay) return;

  var ROOT =
    '[data-im-autoplay], .swiper[data-autoplay]:not([data-autoplay="0"]):not([data-autoplay=""]), announcement-bar[autoplay]';
  var CONTROL =
    '[data-im-scroll], .swiper-button-prev, .swiper-button-next, .swiper-pagination-bullet, [data-im-autoplay-control]';
  var SWIPE_PX = 10;
  var STOPPED = 'data-im-autoplay-stopped';

  function stop(root) {
    if (!root || root.hasAttribute(STOPPED)) return;
    root.setAttribute(STOPPED, '');
    root.style.removeProperty('--swiper-autoplay-delay');

    var swiper = root.swiper;
    if (swiper && swiper.autoplay) {
      if (swiper.autoplay.running) swiper.autoplay.stop();
      // A pause pending on a slide transition leaves `paused` true, and
      // Swiper's visibilitychange handler restarts a paused autoplay when the
      // tab comes back. Stopped means stopped.
      swiper.autoplay.paused = false;
    }

    root.dispatchEvent(new CustomEvent('im:autoplay-stop'));
  }

  // A control's carousel. Some Hyper sections render their arrows beside the
  // Swiper rather than inside it, so fall back to the section's autoplay root.
  function rootOfControl(control) {
    var root = control.closest(ROOT);
    if (root) return root;
    var section = control.closest('.shopify-section');
    return section ? section.querySelector(ROOT) : null;
  }

  var press = null; // { root, x, y } while a press that started in a root is held

  function travelled(x, y) {
    if (!press) return;
    var dx = Math.abs(x - press.x);
    var dy = Math.abs(y - press.y);
    if (dx > SWIPE_PX && dx > dy) {
      stop(press.root);
      press = null;
    } else if (dy > SWIPE_PX && dy >= dx) {
      press = null; // a page scroll, not a swipe
    }
  }

  function release() {
    press = null;
  }

  var capture = { capture: true, passive: true };

  document.addEventListener(
    'click',
    function (event) {
      var control = event.target && event.target.closest ? event.target.closest(CONTROL) : null;
      if (control) stop(rootOfControl(control));
    },
    true
  );

  document.addEventListener(
    'pointerdown',
    function (event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      var root = event.target && event.target.closest ? event.target.closest(ROOT) : null;
      press = root && !root.hasAttribute(STOPPED) ? { root: root, x: event.clientX, y: event.clientY } : null;
    },
    capture
  );

  document.addEventListener(
    'pointermove',
    function (event) {
      if (!press) return;
      // A mouse that was released outside the window never sent pointerup here.
      if (event.pointerType === 'mouse' && !(event.buttons & 1)) return release();
      travelled(event.clientX, event.clientY);
    },
    capture
  );

  // On touch, the browser takes over a native scroll with pointercancel and
  // sends no more pointermoves, so a swipe across a scroll-snap track (the
  // reviews) is only visible as touchmove. pointercancel is therefore NOT a
  // release.
  document.addEventListener(
    'touchmove',
    function (event) {
      var touch = press && event.touches && event.touches[0];
      if (touch) travelled(touch.clientX, touch.clientY);
    },
    capture
  );

  document.addEventListener('pointerup', release, capture);
  document.addEventListener('touchend', release, capture);
  document.addEventListener('touchcancel', release, capture);

  document.addEventListener(
    'wheel',
    function (event) {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) || Math.abs(event.deltaX) < 1) return;
      var root = event.target && event.target.closest ? event.target.closest(ROOT) : null;
      if (root) stop(root);
    },
    capture
  );

  window.ImAutoplay = { stop: stop, ROOT: ROOT };
})();
